import { Alert, Tooltip, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { type ValueOf } from '~/common/types';

import { CompletedIcon, InlineSpinner, WarningIcon } from '@/shared/icons/Icons';
import { type DuplicateCase, getDuplicateCaseLine } from '@/shared/utils/duplicateCase';
import DuplicateCaseListAlert from './DuplicateCaseListAlert';
import { routeFactories } from '@/containers/App/Router/Routes';

export type DuplicateReferenceWarningDisplay = ValueOf<typeof DuplicateReferenceWarningDisplay>;

export const DuplicateReferenceWarningDisplay = {
  Block: 'block',
  Decorator: 'decorator',
} as const;

export const DUPLICATE_REFERENCE_WARNING_TRANSLATION_KEY =
  'repairCaseForm.involvedParties.validation.referenceExistsForInsurer';

type DuplicateReferenceWarningProps = {
  cases: DuplicateCase[];
  display?: DuplicateReferenceWarningDisplay;
  isFetching?: boolean;
  showSuccess?: boolean;
};

/**
 * Renders the outcome of a duplicate insurer-reference check.
 *
 * `display: 'block'` (default) renders an alert per matching case, suited for a section of its
 * own. `display: 'decorator'` renders a single icon with a tooltip carrying the same text instead,
 * suited for use as a form field's `startDecorator`. When a duplicate is found, the icon links to
 * the first matching case (opened in a new tab).
 *
 * @param props.cases - Matching cases, as returned by `useDuplicateReferenceCases`.
 * @param props.display - Which layout to render. Defaults to `'block'`.
 * @param props.isFetching - Whether a lookup is in flight. Renders a loading indicator instead.
 * @param props.showSuccess - Whether to confirm with a success indicator when nothing matched.
 * When false (the default), a clean result renders nothing.
 */
const DuplicateReferenceWarning: React.FC<DuplicateReferenceWarningProps> = ({
  cases,
  display = DuplicateReferenceWarningDisplay.Block,
  isFetching = false,
  showSuccess = false,
}) => {
  const { t } = useTranslation();

  if (display === DuplicateReferenceWarningDisplay.Decorator) {
    if (isFetching)
      return (
        <Tooltip arrow title={t('duplicateCaseCheck.checking')}>
          <Typography component="span">
            <InlineSpinner />
          </Typography>
        </Tooltip>
      );

    if (!cases.length) {
      if (!showSuccess) return null;

      return (
        <Tooltip arrow title={t('duplicateCaseCheck.referenceOk')}>
          <Typography color="success" component="span">
            <CompletedIcon />
          </Typography>
        </Tooltip>
      );
    }

    const [firstDuplicateCase] = cases;
    if (!firstDuplicateCase) return null;

    return (
      <Tooltip
        arrow
        title={
          <div className="flex flex-col gap-1">
            <span>{t(DUPLICATE_REFERENCE_WARNING_TRANSLATION_KEY)}</span>
            {cases.map((duplicateCase) => (
              <span key={duplicateCase.id}>{getDuplicateCaseLine(duplicateCase)}</span>
            ))}
          </div>
        }
      >
        <Typography
          color="warning"
          component={Link}
          target="_blank"
          to={routeFactories.caseDetail({ caseId: firstDuplicateCase.id })}
        >
          <WarningIcon />
        </Typography>
      </Tooltip>
    );
  }

  if (isFetching)
    return (
      <Typography startDecorator={<InlineSpinner />}>{t('duplicateCaseCheck.checking')}</Typography>
    );

  if (!cases.length) {
    if (!showSuccess) return null;

    return (
      <Alert color="success" startDecorator={<CompletedIcon />}>
        {t('duplicateCaseCheck.referenceOk')}
      </Alert>
    );
  }

  return (
    <DuplicateCaseListAlert
      cases={cases}
      color="warning"
      startDecorator={<WarningIcon />}
      title={t(DUPLICATE_REFERENCE_WARNING_TRANSLATION_KEY)}
    />
  );
};

export default DuplicateReferenceWarning;
